import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import { shortOrderId } from "../../lib/format";
import { Link } from "react-router-dom";

/** Stars display inline. */
function StarsDisplay({ value = 0, size = 14 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          fill={i <= value ? "currentColor" : "none"}
          className={i <= value ? "text-yellow-500" : "text-neutral-300"}
        />
      ))}
    </span>
  );
}

const PAGE_SIZE = 20;

export default function AdminRatings() {
  const [data, setData] = useState({ items: [], total: 0, avg_stars: null, avg_driver_stars: null });
  const [page, setPage] = useState(1);
  const skip = (page - 1) * PAGE_SIZE;

  useEffect(() => {
    api.get(`/admin/ratings?limit=${PAGE_SIZE}&skip=${skip}`).then((r) => setData(r.data));
  }, [skip]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-neutral-50">
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6">
        <h1 className="font-display text-4xl font-extrabold">Calificaciones</h1>
        <p className="text-sm text-neutral-500 mt-1">Feedback de los clientes por pedido entregado.</p>

        <div className="mt-6 grid grid-cols-2 gap-3 md:max-w-md">
          <div className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid="ratings-avg-service">
            <p className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Servicio</p>
            <div className="mt-1 flex items-center gap-2">
              <p className="font-display font-extrabold text-3xl tabular-nums">{data.avg_stars ?? "—"}</p>
              {data.avg_stars && <StarsDisplay value={Math.round(data.avg_stars)} size={16} />}
            </div>
            <p className="text-xs text-neutral-500 mt-1">{data.total} calificaciones</p>
          </div>
          <div className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid="ratings-avg-driver">
            <p className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Repartidor</p>
            <div className="mt-1 flex items-center gap-2">
              <p className="font-display font-extrabold text-3xl tabular-nums">{data.avg_driver_stars ?? "—"}</p>
              {data.avg_driver_stars && <StarsDisplay value={Math.round(data.avg_driver_stars)} size={16} />}
            </div>
            <p className="text-xs text-neutral-500 mt-1">Promedio</p>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {data.items.length === 0 ? (
            <p className="text-sm text-neutral-500">Aún no hay calificaciones.</p>
          ) : (
            data.items.map((r) => (
              <Link
                key={r.order_id}
                to={`/admin/orders/${r.order_id}`}
                className="block rounded-xl bg-white border border-neutral-200 hover:border-black p-4 transition-colors"
                data-testid={`rating-row-${r.order_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold truncate">{r.client_name || "Cliente"}</p>
                      <StarsDisplay value={r.stars} />
                      <span className="text-xs text-neutral-400 font-mono">#{shortOrderId(r.order_id)}</span>
                    </div>
                    {r.comment && (
                      <p className="text-sm text-neutral-600 mt-1">“{r.comment}”</p>
                    )}
                    {r.driver_stars && (
                      <p className="text-xs text-neutral-500 mt-1 inline-flex items-center gap-1">
                        Repartidor: <StarsDisplay value={r.driver_stars} size={12} />
                      </p>
                    )}
                  </div>
                  <p className="text-[11px] text-neutral-400 shrink-0 tabular-nums">
                    {new Date(r.created_at).toLocaleDateString("es")}
                  </p>
                </div>
              </Link>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between gap-3">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="h-10"
              data-testid="ratings-page-prev"
            >
              <ChevronLeft size={14} className="mr-1" /> Anterior
            </Button>
            <p className="text-sm text-neutral-600 tabular-nums">
              Página <span className="font-semibold">{page}</span> de <span className="font-semibold">{totalPages}</span>
            </p>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="h-10"
              data-testid="ratings-page-next"
            >
              Siguiente <ChevronRight size={14} className="ml-1" />
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
